import React, { FC } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Column, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';

import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';

import {
  transformViolationChallengeRequest,
  ViolationChallengeFormValuesType,
} from 'QuoteApp/services/endpoints/PostChallengeViolation';
import { useAppContext } from 'QuoteApp/services/providers';
import { getErrorMessage } from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import { ChallengeForm } from 'QuoteApp/components/ReportDetails/ChallengeForm';
import {
  ReportDetailRow,
  getDisplayValue,
} from 'QuoteApp/components/ReportDetails/ReportDetailRow';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks/useQuoteAppFeatureFlags';

type ViolationFormPropsType = {
  driverId: string;
  incidentId: string;
  canChallenge: boolean;
};

export const ViolationForm: FC<ViolationFormPropsType> = ({
  driverId,
  incidentId,
  canChallenge,
}) => {
  const { details, config, setDetails, refreshRate } = useAppContext();
  const { collectDayOfAccidentFlag } = useQuoteAppFeatureFlags();
  const { changeEditingId } = useEdit();
  const { toast } = useToast();
  const identifier = `${incidentId}-violation`;

  const [, challengeViolation] = useLazyQuoteApi<GetDetailsResponseType>({});

  const violation = details.drivers[driverId].violations[incidentId];

  const challengeCommand = details.commands.drivers[driverId].violations[
    incidentId
  ].find(c => c.name === 'challenge_violation');

  if (!config) return null;

  return (
    <Formik<ViolationChallengeFormValuesType>
      enableReinitialize
      initialValues={{
        [identifier]: {
          month: violation.month.value,
          day: collectDayOfAccidentFlag ? violation.day.value : '',
          year: violation.year.value,
          challengeReason: '',
          reasonText: '',
          violationType: violation.violationType.value,
        },
      }}
      validationSchema={Yup.object().shape({
        [identifier]: Yup.object().shape({
          challengeReason: Yup.string().required('Required'),
        }),
      })}
      onSubmit={async values => {
        try {
          const response = await challengeViolation({
            method: 'POST',
            url: challengeCommand?.uri?.replace('/api/v3', ''),
            data: values[identifier],
            transformRequest: transformViolationChallengeRequest,
            transformResponse: transformAppDetails,
          });
          if (response.data.status === 'open') {
            refreshRate();
          }
          changeEditingId(null);
          setDetails(response.data);
        } catch (e) {
          toast({
            type: 'error',
            message: getErrorMessage(e),
            withSecondaryNav: false,
            icon: 'error',
          });
        }
      }}
    >
      {({ values, resetForm }) => {
        return (
          <>
            <ReportDetailRow
              identifier={identifier}
              canChallenge={canChallenge}
              challengeCommand={challengeCommand}
              includeEdit={false}
              includeRemove
              source={violation.sr22.source}
              onRemove={resetForm}
              challengeForm={
                <ChallengeForm
                  variant="remove"
                  identifier={identifier}
                  type="violations"
                  challengeReasonConfig={
                    config.drivers.violations.challengeReasonOptions
                  }
                />
              }
              type="violations"
            >
              <Column variant="quarter" className="fs-mask">
                {getDisplayValue(
                  config.drivers.violations.month[values[identifier].month]
                )}
              </Column>
              {collectDayOfAccidentFlag && (
                <Column variant="quarter" className="fs-mask">
                  {values[identifier].day}
                </Column>
              )}
              <Column variant="quarter" className="fs-mask">
                {getDisplayValue(values[identifier].year)}
              </Column>
              <Column variant="half" className="fs-mask">
                {getDisplayValue(
                  config.drivers.violations.violationType[
                    values[identifier].violationType
                  ]
                )}
              </Column>
            </ReportDetailRow>
          </>
        );
      }}
    </Formik>
  );
};
